import { Timestamp, type Infer } from 'spacetimedb';
import { DbConnection, MyIntentQueueRow, reducers, type ErrorContext, type SubscriptionEventContext } from '../generated';
import type { ConnectionManager } from '../core/ConnectionManager';
import type { ReducerClient } from '../core/ReducerClient';
import type {
  IntentSubmission,
  IntentQueueQuery,
  IntentQueueRecord,
  TickCommitInput,
  Logger
} from '../types';

type MyIntentQueueTableRow = Infer<typeof MyIntentQueueRow>;

export class IntentModule {
  constructor(
    private readonly connectionManager: ConnectionManager,
    private readonly reducerClient: ReducerClient,
    private readonly logger: Logger = () => undefined
  ) {}

  public async enqueueIntent(submission: IntentSubmission): Promise<string> {
    const intentId = submission.intentId ?? generateIntentId();
    const payloadJson = JSON.stringify(submission.payload ?? {});
    const receivedAt = submission.receivedAt ?? new Date();

    await this.reducerClient.callAndConfirm({
      reducerName: 'enqueue_intent',
      paramsType: reducers.enqueueIntent.paramsType,
      args: {
        intentId,
        instanceId: submission.instanceId,
        tickTarget: toInt64Arg(submission.tickTarget),
        payloadJson,
        receivedAt: Timestamp.fromDate(receivedAt)
      },
      onEvent: 'onEnqueueIntent',
      offEvent: 'removeOnEnqueueIntent',
      matchArgs: (args) => args.intentId === intentId,
      timeoutMessage: 'Intent submission timed out',
      failureMessage: 'Failed to enqueue intent'
    });

    return intentId;
  }

  public async clearIntentQueue(characterId: string): Promise<void> {
    await this.reducerClient.callAndConfirm({
      reducerName: 'clear_intent_queue',
      paramsType: reducers.clearIntentQueue.paramsType,
      args: {},
      onEvent: 'onClearIntentQueue',
      offEvent: 'removeOnClearIntentQueue',
      timeoutMessage: 'Clear queue timed out',
      failureMessage: 'Failed to clear intent queue'
    });
  }

  public async fetchIntentQueue(query: IntentQueueQuery): Promise<IntentQueueRecord[]> {
    const connection = await this.connectionManager.ensureConnection();
    const tickTarget = Math.floor(query.tickTarget);
    const sql = 'SELECT * FROM my_intent_queue';

    const result = (await this.runIntentQuery(connection, sql))
      .filter((row) => row.instanceId === query.instanceId && row.tickTarget === tickTarget);

    // Server contract: `my_intent_queue` is already deterministically ordered.
    // Keep this client-side sort as a safety guard for robustness.
    return result.sort((a, b) => {
      const timeDelta = a.receivedAt.getTime() - b.receivedAt.getTime();
      if (timeDelta !== 0) {
        return timeDelta;
      }
      return a.intentId.localeCompare(b.intentId);
    });
  }

  public async persistTickResult(input: TickCommitInput): Promise<void> {
    await this.reducerClient.callFireAndForget(
      'persist_tick_result',
      reducers.persistTickResult.paramsType,
      {
        tickId: input.tickId,
        instanceId: input.instanceId,
        sequence: Math.max(0, Math.floor(input.sequence)),
        eventsJson: JSON.stringify(input.events ?? []),
        entitiesSnapshotJson: JSON.stringify(input.entitiesSnapshot ?? {}),
        hashChainPrev: input.hashChainPrev
      }
    );
  }

  private async runIntentQuery(
    connection: DbConnection,
    sql: string
  ): Promise<IntentQueueRecord[]> {
    return new Promise<IntentQueueRecord[]>((resolve, reject) => {
      const builder = connection.subscriptionBuilder();
      type Handle = ReturnType<typeof builder.subscribe>;
      let handle: Handle | null = null;
      let settled = false;

      builder
        .onApplied((ctx: SubscriptionEventContext) => {
          try {
            const rows = Array.from(ctx.db.myIntentQueue.iter()).map((row: MyIntentQueueTableRow) =>
              this.mapIntentRow(row)
            );
            settled = true;
            if (handle?.unsubscribeThen) {
              handle.unsubscribeThen(() => resolve(rows));
            } else {
              handle?.unsubscribe();
              resolve(rows);
            }
          } catch (error) {
            settled = true;
            handle?.unsubscribe();
            reject(error as Error);
          }
        })
        .onError((ctx: ErrorContext) => {
          if (!settled) {
            settled = true;
            handle?.unsubscribe();
            reject(ctx.event ?? new Error('Intent query failed'));
          }
        });

      handle = builder.subscribe(sql);
    });
  }

  private mapIntentRow(row: MyIntentQueueTableRow): IntentQueueRecord {
    const tickTarget = Number(row.tickTarget);
    if (!Number.isSafeInteger(tickTarget)) {
      throw new Error('Tick target value is outside the safe integer range');
    }
    let payload: unknown;
    try {
      payload = JSON.parse(row.payloadJson ?? '{}');
    } catch (error) {
      this.logger('intent_payload_parse_failed', {
        error: (error as Error).message,
        intentId: row.intentId
      });
      payload = null;
    }

    return {
      intentId: row.intentId,
      characterId: row.characterId,
      instanceId: row.instanceId,
      tickTarget,
      payload,
      receivedAt: row.receivedAt.toDate()
    };
  }
}

function generateIntentId(): string {
  const cryptoApi = (globalThis as typeof globalThis & { crypto?: Crypto }).crypto;
  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }
  const random = Math.floor(Math.random() * 1_000_000)
    .toString(36)
    .padStart(4, '0');
  return 'intent-' + Date.now().toString(36) + '-' + random;
}

function toInt64Arg(value: number): string {
  return String(Math.floor(value));
}
